import type { PlanStatus } from '@/lib/api'

/**
 * Whether the account has NO active plan, so every credit-spending or
 * plan-gated action is refused by the API (CreditService._assert_active_plan,
 * plan_gating deny-by-default). Read from `GET /auth/plan-status`, which every
 * role can call: `plan_name` is null exactly when no entitlement is active.
 *
 * Degrades OPEN: before plan-status has loaded (or if it failed) `planStatus` is
 * null and this is false, so nothing flashes disabled on first paint. The API
 * still refuses the action either way; this only stops the click-then-error.
 */
export function isPlanLapsed(planStatus: Pick<PlanStatus, 'plan_name'> | null | undefined): boolean {
  return planStatus != null && planStatus.plan_name === null
}

/** Why a control is disabled, worded for who is looking at it. */
export function lapsedReason(canRenew: boolean): string {
  return canRenew
    ? 'Your plan has expired. Renew it to keep using this.'
    : 'Your plan has expired. Ask the account owner to renew it.'
}

/**
 * Whether an API failure is the backend's "plan has expired" refusal (a 402
 * from CreditService), so the app can refresh its plan status and flip into the
 * lapsed state without waiting for a reload.
 */
export function isPlanLapsedResponse(status: number | undefined, detail: unknown): boolean {
  return status === 402 && typeof detail === 'string' && detail.toLowerCase().includes('plan has expired')
}
